import { documentMarkupApi } from '../utils/documentMarkupApi';
import { triggerDownload } from '../../../utils/exportUtils';
import { legacyWorkspaceSize } from './markupGeometry';
import { base64ToBytes, isPdfType } from './markupSource';
import { buildAnnotatedPdf } from './annotatedPdf';

/**
 * "Export annotated PDF" for document markups (BBD-126): gathers the file and
 * its strokes, builds the PDF in the browser (annotatedPdf.js) and hands it to
 * the browser as a download. Used by the markup editor and the markup list.
 */

const STROKE_FETCH_CONCURRENCY = 4;

/**
 * Strokes of pages 1..maxPage, a few requests at a time. Pages already in
 * `known` (the editor's own, possibly unsaved, state) are not fetched.
 */
const collectStrokes = async (documentId, maxPage, known = {}) => {
    const strokesByPage = { ...known };
    const missing = [];
    for (let page = 1; page <= maxPage; page++) {
        if (!strokesByPage[page]) missing.push(page);
    }

    const worker = async () => {
        while (missing.length > 0) {
            const page = missing.shift();
            const data = await documentMarkupApi.getStrokes(documentId, page);
            strokesByPage[page] = data.strokes || [];
        }
    };
    await Promise.all(Array.from({ length: STROKE_FETCH_CONCURRENCY }, worker));

    return strokesByPage;
};

export const annotatedFileName = (fileName) => {
    const base = String(fileName || 'document')
        .replace(/\.[a-z0-9]{1,5}$/i, '')
        .replace(/[\\/:*?"<>|]/g, '_')
        .trim();
    return `${base || 'document'} (annotated).pdf`;
};

/**
 * Export one document markup and hand the file to the browser.
 *
 * document: what documentMarkupApi.getDocument() returned when the caller
 *   already has it (the editor); otherwise it is fetched.
 * fileBytes / knownStrokes: likewise, the editor's copy of the file and of the
 *   pages it has loaded.
 *
 * Resolves to { fileName, pageCount, strokeCount, legacyConverted }; rejects
 * with a MarkupExportError (readable message) or a network error.
 */
export const exportAnnotatedMarkup = async ({ documentId, document: loaded = null, fileBytes = null, knownStrokes = {}, onlyAnnotatedPages = false }) => {
    const doc = loaded || await documentMarkupApi.getDocument(documentId);
    const bytes = fileBytes || base64ToBytes(doc.file_data);

    // page_count is the last page that had annotations when the document was
    // fetched; anything drawn since then is in knownStrokes.
    const maxPage = isPdfType(doc.file_type) ? (doc.page_count || 0) : 1;
    const strokesByPage = await collectStrokes(documentId, maxPage, knownStrokes);

    const result = await buildAnnotatedPdf({
        fileBytes: bytes,
        strokesByPage,
        onlyAnnotatedPages,
        legacyWorkspace: legacyWorkspaceSize(),
    });

    const fileName = annotatedFileName(doc.file_name);
    triggerDownload(new Blob([result.bytes], { type: 'application/pdf' }), fileName, 'application/pdf');

    return {
        fileName,
        pageCount: result.pageCount,
        strokeCount: result.strokeCount,
        legacyConverted: result.legacyConverted,
    };
};
